
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { X, Package } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { AddFromInventoryDialog } from "./inventory/AddFromInventoryDialog";
import { useAuth } from "@/components/auth/AuthProvider";
import { GearItem, GearCategory, CAMERA_CATEGORIES, GE_CATEGORIES } from "@/types/gear";
import { useIsMobile } from "@/hooks/use-mobile";

interface GearItemFormProps {
  onClose: () => void;
  onSubmit: (item: Partial<GearItem>) => void;
  initialData?: GearItem;
  showBudget?: boolean;
  department?: "camera" | "ge" | null;
  projectId: string;
}

export default function GearItemForm({
  onClose,
  onSubmit,
  initialData,
  showBudget = true,
  department = "camera",
  projectId,
}: GearItemFormProps) {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [name, setName] = useState(initialData?.name || "");
  const [category, setCategory] = useState<GearCategory | "">(initialData?.category || "");
  const [quantity, setQuantity] = useState(
    initialData?.quantity?.toString() || "1"
  );
  const [costPerUnit, setCostPerUnit] = useState(
    initialData?.cost_per_unit?.toString() || "0"
  );
  const [notes, setNotes] = useState(initialData?.notes || "");
  const [vendorId, setVendorId] = useState(initialData?.vendor_id || "");
  const [showNewVendorInput, setShowNewVendorInput] = useState(false);
  const [newVendorName, setNewVendorName] = useState("");
  const [daysNeeded, setDaysNeeded] = useState(
    initialData?.days_needed?.toString() || "1"
  );
  const [showInventoryDialog, setShowInventoryDialog] = useState(false);

  const { data: vendors, refetch: refetchVendors } = useQuery({
    queryKey: ["vendors"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vendors")
        .select("id, name")
        .order("name");

      if (error) {
        toast.error("Failed to load vendors");
        throw error;
      }

      return data;
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }

    if (!category) {
      toast.error("Category is required");
      return;
    }

    if (!user) {
      toast.error("You must be logged in to add items");
      return;
    }

    let finalVendorId = vendorId;

    if (showNewVendorInput && newVendorName.trim()) {
      try {
        const { data: newVendor, error: vendorError } = await supabase
          .from("vendors")
          .insert([{ 
            name: newVendorName.trim(),
            user_id: user.id 
          }])
          .select()
          .single();

        if (vendorError) {
          console.error("Vendor creation error:", vendorError);
          toast.error("Failed to create new vendor");
          return;
        }

        finalVendorId = newVendor.id;
        await refetchVendors();
        toast.success("New vendor created successfully");
        setShowNewVendorInput(false);
        setVendorId(newVendor.id);
      } catch (error) {
        console.error("Error creating vendor:", error);
        toast.error("Failed to create vendor");
        return;
      }
    }

    try {
      const itemData = {
        ...(initialData?.id ? { id: initialData.id } : {}),
        name: name.trim(),
        category: category as GearCategory,
        quantity: Number(quantity) || 0,
        cost_per_unit: Number(costPerUnit) || 0,
        notes: notes.trim() || null,
        vendor_id: finalVendorId || null,
        days_needed: Number(daysNeeded) || 1,
        project_id: projectId,
      };

      await onSubmit(itemData);
      toast.success(initialData ? "Item updated successfully" : "Item added successfully");
    } catch (error) {
      console.error("Error submitting item:", error);
      toast.error("Failed to save item");
    }
  };

  const handleVendorChange = (value: string) => {
    if (value === "new") {
      setShowNewVendorInput(true);
      setVendorId("");
    } else {
      setShowNewVendorInput(false);
      setVendorId(value);
    }
  };

  const handleInventoryItemsSelect = (items: GearItem[]) => {
    if (items.length === 1) {
      const item = items[0];
      setName(item.name);
      setCategory(item.category);
      setQuantity(item.quantity?.toString() || "1");
      setCostPerUnit(item.cost_per_unit?.toString() || "0");
      setNotes(item.notes || "");
      setShowInventoryDialog(false);
    } else if (items.length > 1) {
      Promise.all(
        items.map((item) =>
          onSubmit({
            name: item.name,
            category: item.category,
            quantity: item.quantity || 1,
            cost_per_unit: item.cost_per_unit || 0,
            notes: item.notes || null,
            vendor_id: vendorId || null,
            days_needed: Number(daysNeeded) || 1,
          })
        )
      );
      onClose();
    }
  };

  const availableCategories = department === "camera" 
    ? CAMERA_CATEGORIES 
    : department === "ge" 
    ? GE_CATEGORIES 
    : [...CAMERA_CATEGORIES, ...GE_CATEGORIES];

  const actionButtons = (
    <div className="flex items-center gap-2">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              onClick={() => setShowInventoryDialog(true)}
              title="Add from Inventory"
            >
              <Package className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            <p>Add from Inventory</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <Button variant="ghost" size="icon" onClick={onClose}>
        <X className="h-4 w-4" />
      </Button>
    </div>
  );

  return (
    <>
      <Card className="w-full max-w-lg">
        <CardHeader className={`flex ${isMobile ? "flex-col" : "flex-row"} items-${isMobile ? "start" : "center"} justify-between`}>
          <CardTitle>
            {initialData ? "Edit Item" : `Add ${department === "camera" ? "Camera" : department === "ge" ? "G&E" : ""} Item`}
          </CardTitle>
          {!isMobile && actionButtons}
          {isMobile && actionButtons}
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={category}
                onValueChange={(value) => setCategory(value as GearCategory)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {availableCategories.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="vendor">Vendor</Label>
              {!showNewVendorInput ? (
                <Select value={vendorId} onValueChange={handleVendorChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors?.map((vendor) => (
                      <SelectItem key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="new">+ Add New Vendor</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter vendor name"
                    value={newVendorName}
                    onChange={(e) => setNewVendorName(e.target.value)}
                    required
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowNewVendorInput(false);
                      setNewVendorName("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name">Item Name</Label>
              <Input
                id="name"
                placeholder="Enter item name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="0"
                  placeholder="Enter quantity"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </div>
              {showBudget && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="costPerUnit">Cost per Unit</Label>
                    <Input
                      id="costPerUnit"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Enter cost per unit"
                      value={costPerUnit}
                      onChange={(e) => setCostPerUnit(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="daysNeeded">Days Needed</Label>
                    <Input
                      id="daysNeeded"
                      type="number"
                      min="1"
                      placeholder="Days needed"
                      value={daysNeeded}
                      onChange={(e) => setDaysNeeded(e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Enter notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </CardContent>
          <CardFooter className="justify-end space-x-2">
            <Button variant="outline" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {initialData ? "Save Changes" : "Add Item"}
            </Button>
          </CardFooter>
        </form>
      </Card>

      <AddFromInventoryDialog
        open={showInventoryDialog}
        onOpenChange={setShowInventoryDialog}
        onAddItems={handleInventoryItemsSelect}
        projectId={projectId}
      />
    </>
  );
}
